import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { 
  IRendererDetails, 
  SituAnalyticsWebApiClient, 
  CreateRendererRequest, 
  UpdateRendererRequest,
  ListRendererPoolsRequest,
  IRendererPool,
  RendererStatus
} from 'situanalytics-webapi-client';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

export interface RendererEditDialogData {
  renderer?: IRendererDetails;
  isNew: boolean;
}

@Component({
  selector: 'app-renderer-edit-dialog',
  templateUrl: './renderer-edit-dialog.component.html',
  styleUrls: ['./renderer-edit-dialog.component.scss']
})
export class RendererEditDialogComponent implements OnInit {
  form: FormGroup;
  loading = false;
  rendererPools: IRendererPool[] = [];
  rendererStatus = RendererStatus;
  statusOptions = [
    { value: RendererStatus.Idle, label: 'Idle' },
    { value: RendererStatus.Busy, label: 'Busy' },
    { value: RendererStatus.Updating, label: 'Updating' },
    { value: RendererStatus.Down, label: 'Down' }
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RendererEditDialogData,
    private dialogRef: MatDialogRef<RendererEditDialogComponent>,
    private fb: FormBuilder,
    private analyticsApi: SituAnalyticsWebApiClient,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.form = this.fb.group({
      name: [data.renderer?.name || '', [Validators.required]],
      canProcessRelease: [data.renderer?.canProcessRelease ?? true],
      rendererPoolId: [data.renderer?.rendererPool?.id || null],
      queueCheckIntervalInMilliseconds: [data.renderer?.queueCheckIntervalInMilliseconds || 5000],
      rendererStatus: [data.renderer?.rendererStatus ?? RendererStatus.Idle]
    });
  }

  ngOnInit(): void {
    this.loadRendererPools();
  }

  private loadRendererPools(): void {
    const request = new ListRendererPoolsRequest();
    // Set paging parameters to get all pools
    request.pageNumber = 1;
    request.pageSize = 100;
    
    this.analyticsApi.listRendererPools(request)
      .then(response => {
        if (response.success && response.items) {
          this.rendererPools = response.items;
        } else {
          this.rendererPools = [];
        }
      })
      .catch(error => {
        console.error('Error loading renderer pools:', error);
        this.rendererPools = [];
      });
  }

  save(): void {
    if (this.form.invalid || this.loading) return;

    this.loading = true;
    const formValue = this.form.value;

    if (this.data.isNew) {
      const request = new CreateRendererRequest();
      request.name = formValue.name;
      request.canProcessRelease = formValue.canProcessRelease;
      request.rendererPoolId = formValue.rendererPoolId || undefined;
      request.queueCheckIntervalInMilliseconds = formValue.queueCheckIntervalInMilliseconds;

      this.analyticsApi.createRenderer(request)
        .then(response => {
          if (response.success) {
            this.showMessage('Renderer created successfully');
            this.dialogRef.close(response.renderer);
          } else {
            this.showMessage('Failed to create renderer');
          }
        })
        .catch(error => {
          console.error('Error creating renderer:', error);
          this.showMessage('Error creating renderer');
        })
        .finally(() => {
          this.loading = false;
        });
    } else {
      const request = new UpdateRendererRequest();
      request.id = this.data.renderer!.id;
      request.name = formValue.name;
      request.canProcessRelease = formValue.canProcessRelease;
      request.rendererPoolId = formValue.rendererPoolId || undefined;
      request.queueCheckIntervalInMilliseconds = formValue.queueCheckIntervalInMilliseconds;
      request.rendererStatus = formValue.rendererStatus;

      this.analyticsApi.updateRenderer(request)
        .then(response => {
          if (response.success) {
            this.showMessage('Renderer updated successfully');
            this.dialogRef.close(response.renderer);
          } else {
            this.showMessage('Failed to update renderer');
          }
        })
        .catch(error => {
          console.error('Error updating renderer:', error);
          this.showMessage('Error updating renderer');
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private showMessage(message: string): void {
    this.snackBar.open(message, 'OK', { duration: 3000 });
  }

  managePools(): void {
    // Close this dialog and navigate to renderer pools page
    this.dialogRef.close();
    this.router.navigate(['/admin/renderer-pools']);
  }
}